import React, { useRef } from "react";

import Image from "next/image";
import Link from "next/link";

import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Column, IFilter, ORDERING, Record } from "@gsmvalve/gsmvalve-table";
import { t, Trans } from "@lingui/macro";
import { Button, Row, Typography } from "antd";
import { keyBy } from "lodash";
import get from "lodash/get";
import type { NextPage } from "next";

import AppLayout from "layout/app";

import EmptyStates from "components/empty-states";
import Exports from "components/export";
import { ActionMenu } from "components/navigations/header";
import Table, { onHasFilters, pageCountMessage, TableSearch } from "components/table";

import { useApplicationContext } from "contexts/app";

import { DATA_TYPE } from "@gsmvalve/gsmvalve-table/dist/cjs/types";
import { User, UserGroup } from "types";

import useOptions from "hooks/options";

const limit = 100;
const sideload = ["user_groups"];

const modifier = (accountURL?: string) => (data: any) => {
  const records: Record[] = [];

  data?.forEach((rows: any) => {
    const row = rows?.data;
    const userGroups = row?.user_groups.filter((group: UserGroup) => group.account === accountURL);
    const keyedGroups = keyBy(userGroups, "url");

    row?.users?.forEach((user: any) => {
      const userGroups = user?.user_groups?.map((g: string) => {
        return keyedGroups?.[g]?.name_display;
      });

      records.push({
        ...user,
        user_groups: userGroups.filter(Boolean).join(", "),
      });
    });
  });

  return records;
};

const filters: IFilter[] = [
  { key: "vehicle", omit: true, title: "" },
  { key: "id", omit: true, title: "" },
  { key: "managed_permissions", omit: true, title: "" },
  { key: "account", omit: true, title: "" },
  { key: "url", omit: true, title: "" },
  { key: "is_integration", omit: true, title: "" },
  { key: "is_active", omit: true, title: "" },
  { key: "user_groups", omit: true, title: "" },
  { key: "user_group", omit: true, title: "" },
  { key: "date_joined", omit: true, title: "" },
  { key: "last_login", omit: true, title: "" },
  { key: "display", omit: true, title: "" },
];

const Users: NextPage = () => {
  const optionsUrl = "reports/users/";
  const columns: Column[] = [
    {
      key: "username",
      title: t`Username`,
      isVisible: true,
      dataIndex: ["username"],
      fixed: true,
      isSortable: true,
      render: (data) => {
        return (
          <Typography.Text ellipsis={true}>
            <Link href={`/settings/users/${data?.id}/?tab=overview`}>{data?.username}</Link>
          </Typography.Text>
        );
      },
    },
    { key: "first_name", title: t`First name`, isVisible: true, dataIndex: ["first_name"] },
    {
      key: "last_name",
      title: t`Last name`,
      isVisible: true,
      dataIndex: ["last_name"],
    },
    {
      key: "date_joined",
      title: t`Date Joined`,
      isVisible: true,
      dataIndex: ["date_joined"],
    },
    {
      key: "last_login",
      title: t`Last Login`,
      isVisible: true,
      dataIndex: ["last_login"],
    },
    {
      key: "user_groups",
      title: t`User groups`,
      isVisible: true,
      dataIndex: ["user_groups"],
      type: DATA_TYPE.TEXT,
      width: 500,
    },
    {
      key: "actions",
      title: t`Actions`,
      width: 90,
      render: (data) => {
        return (
          <Link href={`/settings/users/${data?.id}/?tab=overview`}>
            <Button type={"text"} icon={<EditOutlined />} />
          </Link>
        );
      },
      isVisible: false,
    },
    { key: "eco_score_enabled", title: t`Eco score enabled` },
    { key: "external_id", title: t`External id` },
    { key: "status", title: t`Status` },
    { key: "phone", title: t`Phone` },
    { key: "email", title: t`Email` },
    { key: "is_active", title: t`Active` },
    { key: "is_integration", title: t`Is integration` },
    { key: "name_display", omit: true },
    { key: "url", omit: true },
  ];

  const tableCallback = useRef<any>(null);
  const { session, configuration } = useApplicationContext();
  const { result: options, loading } = useOptions(optionsUrl, "");

  const accountId = get(configuration, "account.id", "");
  const accountURL = get(configuration, "account.url", "");
  const url = `users/?account=${accountId}&ordering=username&sideload=${sideload.toString()}&count=true`;
  const newUserButton = (
    <Link href={"/settings/users/new/"}>
      <Button type="primary" icon={<PlusCircleOutlined />}>
        <span>
          <Trans>New user</Trans>
        </span>
      </Button>
    </Link>
  );

  return (
    <AppLayout fullscreen={true} pageTitle={t`Users`}>
      <ActionMenu
        menus={[
          <Row>
            <TableSearch placeholder={t`Search users...`} />
          </Row>,
          <Exports
            optionsUrl={optionsUrl}
            formats={options?.formats}
            loading={loading}
            callback={(fn: any) => {
              if (tableCallback?.current) {
                tableCallback?.current((columns: string[]) => {
                  fn(columns);
                });
              }
            }}
            ordering={["username"]}
          />,
          newUserButton,
        ]}
      />
      <Table<User[]>
        key={"users"}
        id={`${session?.accountId}-users`}
        columns={{
          filtersPath: "/users/",
          path: "PublicUser",
          customColumns: columns,
          customFilters: filters,
        }}
        records={{
          url: url,
          sort: [{ key: "username", order: "asc" as ORDERING }],
          limit: limit,
          modifier: modifier(accountURL),
        }}
        callback={tableCallback}
        empty={{
          emptyWithoutFilter: (
            <EmptyStates
              title={t`No users yet`}
              description={t`Add people in charge of managing vehicles, drivers, groups, accounts and reports.`}
              // @ts-ignore
              icon={<Image src={"/images/users-empty-state.svg"} width={260} height={260} alt={t`users empty state`} />}
              actions={[newUserButton]}
            />
          ),
          onHasFilter: onHasFilters(),
        }}
        controls={{
          manageColumn: true,
          filterBtn: true,
          pageCountMessage: pageCountMessage(t`Users`),
        }}
      />
    </AppLayout>
  );
};

export default Users;
